import React, { useContext } from 'react'
import { withStyles } from '@mui/styles'
import { Dialog, Box, IconButton } from '@mui/material'

import CloseIcon from '@mui/icons-material/Close'

import { useWidth } from '../../../core/utils'
import { ModalContext } from '../contexts'
import { TYPES } from '../constants'
import { modalStyles } from './modal.styles'

/**
 * @typedef {object} ModalComponentProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por withStyles.
 */

/**
 * Componente modal gestionado mediante contexto (ModalContext).
 *
 * Modal configurable con soporte para fullscreen, modo responsivo, tamaños personalizados,
 * y contenido dinámico. Consume el ModalContext para obtener estado, tipo, componente a renderizar,
 * y acciones de cierre. Incluye botón de cierre opcional.
 *
 * @component
 * @example
 * // El modal se gestiona mediante el ModalContext, no se usa directamente
 * // Ejemplo de uso del contexto:
 * const { actions } = useContext(ModalContext);
 * actions.open({
 *   type: 'RESPONSIVE',
 *   Component: MyModalContent,
 *   data: { userId: 123 },
 *   maxWidth: 'md',
 *   closable: true
 * });
 *
 * @param {ModalComponentProps} props - Las props del componente ModalComponent.
 */
export const ModalComponent = withStyles(modalStyles)(({ classes }) => {
  const {
    actions: { close },
    isOpen,
    type,
    Component,
    data,
    maxWidth,
    closable = false,
  } = useContext(ModalContext)

  const isMobileWidth = useWidth() === 'xs'

  return (
    <Dialog
      open={isOpen}
      fullScreen={type === TYPES.FULL_SCREEN || (type === TYPES.RESPONSIVE && isMobileWidth)}
      className={classes[type]}
      maxWidth={maxWidth}
      fullWidth
    >
      {closable ? (
        <Box display="flex" justifyContent="flex-end">
          <IconButton onClick={close}>
            <CloseIcon />
          </IconButton>
        </Box>
      ) : null}
      {Component ? <Component close={close} {...data} /> : null}
    </Dialog>
  )
})
